import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { collection, getDocs, query, where, orderBy, doc, getDoc, documentId } from 'firebase/firestore';
import { db, auth } from './firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWithinInterval, subMonths, eachDayOfInterval, isSameMonth, isSameDay, isToday, addMonths } from 'date-fns';
import { useNavigate } from 'react-router-dom';

// Firestore `in` queries accept at most 30 values per query.
const IN_QUERY_LIMIT = 30;

// Ceiling on how many packages we eagerly resolve for the selected period, so
// "All Time" doesn't fan out into hundreds of lookups.
const PACKAGE_PREFETCH_LIMIT = 600;

// Payments were written by different versions of the app, so paymentDate can be
// a Timestamp, a {seconds} shape, or a console-pasted string. Anything we can't
// parse returns null rather than "now" — dropping it out of the date views is
// honest, bucketing it onto today is not.
const toJsDate = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
  if (typeof raw.toDate === 'function') return raw.toDate();
  if (typeof raw.seconds === 'number') return new Date(raw.seconds * 1000);
  if (typeof raw === 'string') {
    const cleaned = raw
      .replace(/ at /i, ' ')
      .replace(/ utc-4/i, ' GMT-0400')
      .replace(/ utc/i, ' GMT');
    let parsed = new Date(cleaned);
    if (isNaN(parsed.getTime())) parsed = new Date(raw);
    if (isNaN(parsed.getTime())) {
      console.warn('Could not parse payment date:', raw);
      return null;
    }
    return parsed;
  }
  return null;
};

// Calendar cells are local days. toISOString() keys them in UTC, which pushes
// an evening payment onto the next day's cell.
const dateKey = (date) => format(date, 'yyyy-MM-dd');

const mapPaymentDoc = (snapshot) => {
  const data = snapshot.data();
  return { id: snapshot.id, ...data, paymentDate: toJsDate(data.paymentDate) };
};

const mergePayments = (previous, incoming) => {
  const byId = new Map(previous.map(p => [p.id, p]));
  incoming.forEach(p => byId.set(p.id, p));
  return [...byId.values()].sort(
    (a, b) => (b.paymentDate?.getTime() || 0) - (a.paymentDate?.getTime() || 0)
  );
};

const chunk = (list, size) => {
  const chunks = [];
  for (let i = 0; i < list.length; i += size) chunks.push(list.slice(i, i + size));
  return chunks;
};

const matchesSearch = (payment, pkg, term) => (
  pkg?.recipient?.toLowerCase().includes(term) ||
  pkg?.trackingNumber?.toLowerCase().includes(term) ||
  payment.reference?.toLowerCase().includes(term) ||
  payment.notes?.toLowerCase().includes(term) ||
  payment.amount?.toString().includes(term)
);

const PaymentHistory = () => {
  const [packages, setPackages] = useState({});
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [user, authLoading] = useAuthState(auth);
  const navigate = useNavigate();

  // Batch caching states
  const [paymentCache, setPaymentCache] = useState([]); // All fetched payments cached here
  const [cacheRange, setCacheRange] = useState(null); // Date span the cache is known to cover
  const [cacheInitialized, setCacheInitialized] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  // Refs keep the fetch helpers identity-stable so they don't retrigger the
  // effects that call them, and let overlapping requests cooperate.
  const cacheRangeRef = useRef(null);
  const fetchQueue = useRef(Promise.resolve());
  const requestedPackageIds = useRef(new Set());
  const requestedUserIds = useRef(new Set());

  // Time period states
  const [selectedPeriod, setSelectedPeriod] = useState('1M'); // Default to current month
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Custom date range picker states
  const [rangePickerMonth, setRangePickerMonth] = useState(new Date());
  const [dateSelectionMode, setDateSelectionMode] = useState('start'); // 'start' or 'end'
  const [showCalendarDropdown, setShowCalendarDropdown] = useState(false);

  // Calendar states
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);

  // Filter states
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [recordedByFilter, setRecordedByFilter] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Payment method styles
  const paymentMethodStyles = {
    'credit_card': { 
      color: 'bg-blue-100 text-blue-800',
      icon: '💳'
    },
    'bank_transfer': { 
      color: 'bg-green-100 text-green-800',
      icon: '🏦'
    },
    'cash': { 
      color: 'bg-yellow-100 text-yellow-800',
      icon: '💵'
    },
    'check': { 
      color: 'bg-purple-100 text-purple-800',
      icon: '📝'
    },
    'default': { 
      color: 'bg-gray-100 text-gray-800',
      icon: '💰'
    }
  };

  // Check if user is logged in. Firebase restores the session asynchronously,
  // so waiting on authLoading avoids bouncing a signed-in user to /signin on
  // the first render.
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/signin');
    }
  }, [user, authLoading, navigate]);

  // Helper function to fetch packages for specific payments.
  // Both the id bookkeeping and the state update have to survive overlapping
  // calls: the previous version snapshotted `packages` from its closure and
  // replaced the whole map, so whichever call resolved last discarded the
  // other's results and rows rendered as N/A.
  const fetchPackagesForPayments = useCallback(async (paymentsList) => {
    const missing = [...new Set(paymentsList.map(p => p.packageId).filter(Boolean))]
      .filter(id => !requestedPackageIds.current.has(id));

    if (missing.length === 0) return;
    missing.forEach(id => requestedPackageIds.current.add(id));

    const fetched = {};
    for (const ids of chunk(missing, IN_QUERY_LIMIT)) {
      try {
        const packagesSnapshot = await getDocs(query(
          collection(db, 'packages'),
          where(documentId(), 'in', ids)
        ));
        packagesSnapshot.docs.forEach(snapshot => {
          fetched[snapshot.id] = { id: snapshot.id, ...snapshot.data() };
        });
      } catch (error) {
        console.error("Error fetching packages chunk:", error);
        // Let a later render retry these instead of caching the failure.
        ids.forEach(id => requestedPackageIds.current.delete(id));
      }
    }

    if (Object.keys(fetched).length > 0) {
      setPackages(prev => ({ ...prev, ...fetched }));
    }
  }, []);

  // Helper function to fetch users
  const fetchUsers = useCallback(async (userIds) => {
    const missing = [...new Set(userIds)]
      .filter(id => id && !requestedUserIds.current.has(id));

    if (missing.length === 0) return;
    missing.forEach(id => requestedUserIds.current.add(id));

    const fetched = {};
    for (const ids of chunk(missing, IN_QUERY_LIMIT)) {
      try {
        const usersSnapshot = await getDocs(query(
          collection(db, 'users'),
          where(documentId(), 'in', ids)
        ));
        usersSnapshot.docs.forEach(snapshot => {
          fetched[snapshot.id] = { id: snapshot.id, ...snapshot.data() };
        });
      } catch (error) {
        console.error("Error fetching users chunk:", error);
        for (const userId of ids) {
          try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
              fetched[userId] = { id: userId, ...userDoc.data() };
            }
          } catch (individualError) {
            console.error(`Error fetching user ${userId}:`, individualError);
            requestedUserIds.current.delete(userId);
          }
        }
      }
    }

    if (Object.keys(fetched).length > 0) {
      setUsers(prev => ({ ...prev, ...fetched }));
    }
  }, []);

  // Fetch one contiguous span of payments.
  const fetchSpan = useCallback(async (start, end) => {
    const paymentsSnapshot = await getDocs(query(
      collection(db, 'payments'),
      where('paymentDate', '>=', start),
      where('paymentDate', '<=', end),
      orderBy('paymentDate', 'desc')
    ));
    return paymentsSnapshot.docs.map(mapPaymentDoc);
  }, []);

  // Guarantee the cache covers [start, end], fetching only the gaps on either
  // side of what's already cached. Requests are queued rather than dropped when
  // one is already in flight — the old guard silently discarded them, which is
  // why widening the range often left the view empty.
  const ensureRange = useCallback((start, end) => {
    fetchQueue.current = fetchQueue.current.then(async () => {
      const covered = cacheRangeRef.current;
      const gaps = [];

      if (!covered) {
        gaps.push([start, end]);
      } else {
        if (start < covered.start) gaps.push([start, new Date(covered.start.getTime() - 1)]);
        if (end > covered.end) gaps.push([new Date(covered.end.getTime() + 1), end]);
      }

      if (gaps.length === 0) return;

      setIsFetchingMore(true);
      try {
        const fetched = (await Promise.all(gaps.map(([s, e]) => fetchSpan(s, e)))).flat();

        if (fetched.length > 0) {
          setPaymentCache(prev => mergePayments(prev, fetched));
        }

        const nextRange = {
          start: covered && covered.start < start ? covered.start : start,
          end: covered && covered.end > end ? covered.end : end
        };
        cacheRangeRef.current = nextRange;
        setCacheRange(nextRange);

        await fetchUsers(fetched.flatMap(p => [p.paidBy, p.recordedBy]));
      } catch (error) {
        console.error("Error fetching payment data:", error);
      } finally {
        setIsFetchingMore(false);
      }
    });

    return fetchQueue.current;
  }, [fetchSpan, fetchUsers]);

  // Date span the selected time filter asks for. 'Month' is what browsing the
  // calendar puts you in: the scope becomes whichever month is on screen, so
  // the summary cards always describe the month you are actually looking at.
  const periodRange = useMemo(() => {
    const now = new Date();
    switch (selectedPeriod) {
      case '1D':
        return { start: startOfDay(now), end: endOfDay(now) };
      case '1W':
        return { start: startOfWeek(now), end: endOfWeek(now) };
      case '1M':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case '1Y':
        return { start: startOfMonth(subMonths(now, 11)), end: endOfMonth(now) };
      case 'Month':
        return { start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) };
      case 'Custom':
        if (customStartDate && customEndDate) {
          return { start: startOfDay(customStartDate), end: endOfDay(customEndDate) };
        }
        // Default to last 3 months if custom dates not set
        return { start: startOfMonth(subMonths(now, 2)), end: endOfMonth(now) };
      case 'All':
        return { start: new Date(0), end: endOfDay(now) };
      default:
        return { start: startOfMonth(now), end: endOfMonth(now) };
    }
  }, [selectedPeriod, customStartDate, customEndDate, currentMonth]);

  // What the summary cards are currently counting, in words.
  const scopeLabel = useMemo(() => {
    if (selectedDate) return format(selectedDate, 'MMMM d, yyyy');
    switch (selectedPeriod) {
      case '1D': return 'Today';
      case '1W': return 'This week';
      case '1M': return 'This month';
      case '1Y': return 'Last 12 months';
      case 'Month': return format(currentMonth, 'MMMM yyyy');
      case 'All': return 'All time';
      case 'Custom':
        return customStartDate && customEndDate
          ? `${format(customStartDate, 'MMM d, yyyy')} – ${format(customEndDate, 'MMM d, yyyy')}`
          : 'Custom range';
      default: return '';
    }
  }, [selectedPeriod, selectedDate, currentMonth, customStartDate, customEndDate]);

  // Span the calendar grid is showing, including the leading/trailing days.
  const calendarRange = useMemo(() => ({
    start: startOfWeek(startOfMonth(currentMonth)),
    end: endOfWeek(endOfMonth(currentMonth))
  }), [currentMonth]);

  // The calendar navigates independently of the time filter, so the cache has
  // to cover both. Previously only the filter drove fetching, which left every
  // month outside the selected period blank.
  useEffect(() => {
    if (!user) return;

    const start = periodRange.start < calendarRange.start ? periodRange.start : calendarRange.start;
    const end = periodRange.end > calendarRange.end ? periodRange.end : calendarRange.end;

    let active = true;
    ensureRange(start, end).then(() => {
      if (!active) return;
      setCacheInitialized(true);
      setLoading(false);
    });

    return () => { active = false; };
  }, [user, periodRange, calendarRange, ensureRange]);

  // Calendar buckets come from the whole cache, not the period-filtered slice,
  // so browsing to another month shows its payments.
  const paymentsByDate = useMemo(() => {
    const map = {};
    paymentCache.forEach(payment => {
      if (!payment.paymentDate) return;
      const key = dateKey(payment.paymentDate);
      if (!map[key]) map[key] = [];
      map[key].push(payment);
    });
    return map;
  }, [paymentCache]);

  // Payments inside the selected time filter.
  const payments = useMemo(
    () => paymentCache.filter(p => p.paymentDate && isWithinInterval(p.paymentDate, periodRange)),
    [paymentCache, periodRange]
  );

  // Function to refresh cache with latest payments (useful after adding new payments)
  const refreshCache = useCallback(async () => {
    if (!user) return;

    setIsFetchingMore(true);
    try {
      // Re-read the current month so edits and deletions are picked up too.
      const start = startOfMonth(subMonths(new Date(), 1));
      const end = endOfDay(new Date());
      const refreshed = await fetchSpan(start, end);
      const refreshedIds = new Set(refreshed.map(p => p.id));

      setPaymentCache(prev => mergePayments(
        // Drop stale copies of anything in the refreshed span that no longer exists.
        prev.filter(p => refreshedIds.has(p.id) || !p.paymentDate || p.paymentDate < start),
        refreshed
      ));

      await fetchUsers(refreshed.flatMap(p => [p.paidBy, p.recordedBy]));
    } catch (error) {
      console.error("Error refreshing payment cache:", error);
    } finally {
      setIsFetchingMore(false);
    }
  }, [user, fetchSpan, fetchUsers]);

  // Payments shown in the table / day modal, after every non-date filter.
  const filteredPayments = useMemo(() => {
    // A clicked calendar day is answered from the full cache so the day view
    // still works when that day falls outside the selected time filter.
    let filtered = selectedDate
      ? (paymentsByDate[dateKey(selectedDate)] || [])
      : payments;

    if (paymentMethodFilter !== 'all') {
      filtered = filtered.filter(payment => payment.paymentMethod === paymentMethodFilter);
    }

    if (searchQuery.trim()) {
      const term = searchQuery.toLowerCase();
      filtered = filtered.filter(payment => matchesSearch(payment, packages[payment.packageId], term));
    }

    if (recordedByFilter) {
      filtered = filtered.filter(payment =>
        payment.recordedBy === recordedByFilter || payment.paidBy === recordedByFilter
      );
    }

    return filtered;
  }, [payments, paymentsByDate, selectedDate, paymentMethodFilter, searchQuery, recordedByFilter, packages]);

  // Calendar days holding a search match.
  const highlightedDates = useMemo(() => {
    if (!searchQuery.trim()) return new Set();
    const term = searchQuery.toLowerCase();
    return new Set(
      payments
        .filter(payment => matchesSearch(payment, packages[payment.packageId], term))
        .map(payment => dateKey(payment.paymentDate))
    );
  }, [payments, packages, searchQuery]);

  // Packages back the Recipient/Tracking columns and the search, so load them
  // for the whole period rather than only the visible page — a match on page 4
  // is invisible if page 4's packages were never fetched. Capped so "All Time"
  // can't turn into hundreds of lookups; anything past the cap is covered by
  // the per-page fetch below.
  useEffect(() => {
    if (payments.length > 0) fetchPackagesForPayments(payments.slice(0, PACKAGE_PREFETCH_LIMIT));
  }, [payments, fetchPackagesForPayments]);

  // Whatever page is on screen always gets its packages, cap or not.
  useEffect(() => {
    const pageItems = filteredPayments.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    if (pageItems.length > 0) fetchPackagesForPayments(pageItems);
  }, [filteredPayments, currentPage, pageSize, fetchPackagesForPayments]);

  // Reset to the first page whenever the result set is redefined.
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedPeriod, currentMonth, customStartDate, customEndDate, selectedDate, paymentMethodFilter, searchQuery, recordedByFilter, pageSize]);

  // Calculate totals for different periods based on filtered payments
  const calculatePeriodTotals = () => {
    if (!filteredPayments || filteredPayments.length === 0) {
      return { daily: 0, weekly: 0, monthly: 0 };
    }

    // Get the date range from filtered payments
    const paymentDates = filteredPayments
      .filter(p => p.paymentDate)
      .map(p => p.paymentDate)
      .sort((a, b) => a - b);

    if (paymentDates.length === 0) {
      return { daily: 0, weekly: 0, monthly: 0 };
    }

    const firstDate = paymentDates[0];
    const lastDate = paymentDates[paymentDates.length - 1];
    const daysDiff = Math.ceil((lastDate - firstDate) / (1000 * 60 * 60 * 24)) + 1;
    const weeksDiff = Math.max(1, Math.ceil(daysDiff / 7));
    const monthsDiff = Math.max(1, Math.ceil(daysDiff / 30));

    const totalAmount = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

    // Calculate averages based on the date range
    return {
      daily: daysDiff > 0 ? totalAmount / daysDiff : totalAmount,
      weekly: weeksDiff > 0 ? totalAmount / weeksDiff : totalAmount,
      monthly: monthsDiff > 0 ? totalAmount / monthsDiff : totalAmount
    };
  };

  const periodTotals = calculatePeriodTotals();
  const totalAmount = filteredPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
  // If no status field exists, treat payments as 'paid' by default, otherwise check for explicit status
  const paidAmount = filteredPayments.filter(p => !p.status || p.status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0);
  const pendingAmount = filteredPayments.filter(p => p.status === 'pending').reduce((sum, p) => sum + (p.amount || 0), 0);
  
  // Pagination
  const totalPages = Math.ceil(filteredPayments.length / pageSize);
  const paginatedPayments = filteredPayments.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Calendar functions
  const getDaysInMonth = () => {
    const start = startOfWeek(startOfMonth(currentMonth));
    const end = endOfWeek(endOfMonth(currentMonth));
    return eachDayOfInterval({ start, end });
  };

  const getPaymentCountForDate = (date) => paymentsByDate[dateKey(date)]?.length || 0;

  // Removed getTotalAmountForDate - kept for potential future use in calendar tooltips

  const handleDateClick = (date) => {
    const paymentCount = getPaymentCountForDate(date);
    if (paymentCount > 0) {
      setSelectedDate(date);
      setRecordedByFilter(''); // Reset filter when selecting new date
      setShowPaymentDetails(true);
    }
  };

  // Browsing to another month re-scopes the summary cards to that month, and
  // drops any day selection left over from the month we came from.
  const goToMonth = (month) => {
    setCurrentMonth(month);
    setSelectedPeriod('Month');
    setSelectedDate(null);
    setShowDatePicker(false);
  };

  // Picking a period pulls the calendar to that period's month, so the grid and
  // the summary cards never describe two different stretches of time.
  const selectPeriod = (period, anchorMonth = new Date()) => {
    setSelectedPeriod(period);
    setCurrentMonth(anchorMonth);
    setSelectedDate(null);
    setShowDatePicker(false);
  };

  const handlePreviousMonth = () => goToMonth(subMonths(currentMonth, 1));

  const handleNextMonth = () => goToMonth(addMonths(currentMonth, 1));

  const handleToday = () => goToMonth(new Date());

  // Range picker calendar functions
  const getDaysInRangePickerMonth = () => {
    const start = startOfWeek(startOfMonth(rangePickerMonth));
    const end = endOfWeek(endOfMonth(rangePickerMonth));
    return eachDayOfInterval({ start, end });
  };

  const handleRangePickerPreviousMonth = () => {
    setRangePickerMonth(prev => subMonths(prev, 1));
  };

  const handleRangePickerNextMonth = () => {
    setRangePickerMonth(prev => addMonths(prev, 1));
  };

  const handleRangeDateClick = (date) => {
    if (dateSelectionMode === 'start') {
      setCustomStartDate(date);
      setCustomEndDate(null); // Reset end date when selecting new start
      setDateSelectionMode('end');
    } else {
      // If selecting end date and it's before start, swap them
      if (customStartDate && date < customStartDate) {
        setCustomEndDate(customStartDate);
        setCustomStartDate(date);
      } else {
        setCustomEndDate(date);
      }
      // Keep on 'end' mode so user can adjust if needed
    }
  };

  const isDateInRange = (date) => {
    if (!customStartDate || !customEndDate) return false;
    return date >= startOfDay(customStartDate) && date <= endOfDay(customEndDate);
  };

  const isDateRangeStart = (date) => {
    return customStartDate && isSameDay(date, customStartDate);
  };

  const isDateRangeEnd = (date) => {
    return customEndDate && isSameDay(date, customEndDate);
  };

  // Navigate to package details
  const navigateToPackage = (packageId) => {
    if (packageId) {
      navigate(`/package/${packageId}`);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Get payment method display info
  const getPaymentMethodInfo = (method) => {
    return paymentMethodStyles[method] || paymentMethodStyles.default;
  };

  // Format payment method for display
  const formatPaymentMethod = (method) => {
    if (!method) return 'Unknown';
    return method.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Get user name
  const getUserName = (userId) => {
    if (!userId) return 'Unknown';
    const user = users[userId];
    if (!user) return 'Unknown User';
    return user.name || user.email || user.displayName || userId || 'Unknown';
  };

  // Calculate totals
  const totalCount = filteredPayments.length;

  // Get unique payment methods for filter
  const uniquePaymentMethods = [...new Set(payments.map(p => p.paymentMethod).filter(Boolean))];

  if (loading) {
    return (
      <div className="w-full flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex justify-center">
      <div className="w-full max-w-7xl p-4 md:p-6">
        <header className="flex flex-col sm:flex-row justify-between items-center mb-4 md:mb-6 gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold">Payment History</h1>
            {/* Refresh Cache Button */}
            <button
              onClick={refreshCache}
              disabled={isFetchingMore}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh payment data"
            >
              <svg 
                className={`w-5 h-5 text-gray-600 ${isFetchingMore ? 'animate-spin' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
          
          {/* Time Period Selector */}
          <div className="flex flex-wrap items-center gap-2 bg-white rounded-lg shadow-sm border border-gray-200 p-1">
            {[
              { period: '1D', label: 'Today' },
              { period: '1W', label: 'This Week' },
              { period: '1M', label: 'This Month' },
              { period: '1Y', label: '1 Year' }
            ].map(({ period, label }) => (
              <button
                key={period}
                onClick={() => selectPeriod(period)}
                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                  selectedPeriod === period
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {label}
              </button>
            ))}
            <button
              onClick={() => {
                setShowDatePicker(!showDatePicker);
                if (!showDatePicker) {
                  setSelectedPeriod('Custom');
                }
              }}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                selectedPeriod === 'Custom' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              📅 Custom
            </button>
            <button
              onClick={() => {
                if (selectedPeriod === 'All') {
                  // If already on All, don't reload
                  return;
                }
                const confirmLoad = window.confirm(
                  '⚠️ Loading all payment history may take a while and use significant data. Continue?'
                );
                if (confirmLoad) {
                  selectPeriod('All');
                }
              }}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                selectedPeriod === 'All' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              All Time
            </button>
          </div>
        </header>

        {/* Loading Indicator for Additional Data */}
        {isFetchingMore && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <svg className="animate-spin h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Fetching additional payment data...</span>
            </div>
          </div>
        )}

        {/* Cache Status Indicator */}
        {cacheInitialized && !loading && (
          <div className="mb-4 text-xs text-gray-500 text-right">
            📦 Cached: {paymentCache.length} payments
            {cacheRange && ` • Covering ${format(cacheRange.start, 'MMM d, yyyy')} – ${format(cacheRange.end, 'MMM d, yyyy')}`}
          </div>
        )}

        {/* Custom Date Range Picker - Flowbite Style */}
        {showDatePicker && (
          <div className="mb-6">
            {/* Date Range Input Fields */}
            <div className="flex flex-col md:flex-row md:items-center bg-white rounded-lg shadow-sm border border-gray-200 p-4 gap-4">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z"/>
                  </svg>
                </div>
                <input
                  id="datepicker-range-start"
                  name="start"
                  type="text"
                  readOnly
                  value={customStartDate ? format(customStartDate, 'MMM dd, yyyy') : ''}
                  onClick={() => {
                    setDateSelectionMode('start');
                    setShowCalendarDropdown(true);
                  }}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5 cursor-pointer hover:bg-gray-100 transition-colors"
                  placeholder="Select date start"
                />
                
                {/* Calendar Dropdown for Start Date */}
                {showCalendarDropdown && dateSelectionMode === 'start' && (
                  <>
                    {/* Backdrop to close dropdown when clicking outside */}
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowCalendarDropdown(false)}
                    />
                    
                    {/* Calendar Popup - Centered on mobile, under input on desktop */}
                    <div className="fixed md:absolute left-1/2 md:left-0 top-1/2 md:top-full -translate-x-1/2 md:translate-x-0 -translate-y-1/2 md:translate-y-0 md:mt-2 z-20 bg-white rounded-lg shadow-xl border border-gray-200 p-3 w-[calc(100vw-2rem)] max-w-[280px] md:w-72">
                      {/* Calendar Navigation */}
                      <div className="flex items-center justify-between mb-3">
                        <button
                          onClick={handleRangePickerPreviousMonth}
                          className="p-1.5 hover:bg-gray-100 rounded transition-colors text-gray-700 flex-shrink-0"
                          title="Previous month"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <h4 className="text-sm font-semibold text-gray-900 truncate px-2">
                          {format(rangePickerMonth, 'MMMM yyyy')}
                        </h4>
                        <button
                          onClick={handleRangePickerNextMonth}
                          className="p-1.5 hover:bg-gray-100 rounded transition-colors text-gray-700 flex-shrink-0"
                          title="Next month"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>

                      {/* Calendar Grid */}
                      <div>
                        <div className="grid grid-cols-7 gap-1 mb-1">
                          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                            <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                              {day}
                            </div>
                          ))}
                        </div>
                        
                        <div className="grid grid-cols-7 gap-1">
                          {getDaysInRangePickerMonth().map((day, idx) => {
                            const isCurrentMonth = isSameMonth(day, rangePickerMonth);
                            const isTodayDate = isToday(day);
                            const isStart = isDateRangeStart(day);
                            const isEnd = isDateRangeEnd(day);
                            const inRange = isDateInRange(day);
                            
                            return (
                              <button
                                key={idx}
                                onClick={() => {
                                  if (isCurrentMonth) {
                                    handleRangeDateClick(day);
                                  }
                                }}
                                disabled={!isCurrentMonth}
                                className={`
                                  relative p-1.5 h-8 rounded text-xs font-medium transition-all
                                  ${!isCurrentMonth ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-blue-50 cursor-pointer'}
                                  ${isTodayDate && isCurrentMonth ? 'ring-1 ring-blue-500' : ''}
                                  ${isStart || isEnd ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}
                                  ${inRange && !isStart && !isEnd && isCurrentMonth ? 'bg-blue-100 text-blue-900' : ''}
                                `}
                              >
                                {format(day, 'd')}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Quick Select Options */}
                      <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 gap-1.5">
                        <button
                          onClick={() => {
                            const end = new Date();
                            const start = subMonths(end, 1);
                            setCustomStartDate(start);
                            setCustomEndDate(end);
                            setDateSelectionMode('end');
                          }}
                          className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors whitespace-nowrap"
                        >
                          Last Month
                        </button>
                        <button
                          onClick={() => {
                            const end = new Date();
                            const start = subMonths(end, 3);
                            setCustomStartDate(start);
                            setCustomEndDate(end);
                            setDateSelectionMode('end');
                          }}
                          className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors whitespace-nowrap"
                        >
                          Last 3M
                        </button>
                        <button
                          onClick={() => {
                            const end = new Date();
                            const start = subMonths(end, 6);
                            setCustomStartDate(start);
                            setCustomEndDate(end);
                            setDateSelectionMode('end');
                          }}
                          className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors whitespace-nowrap"
                        >
                          Last 6M
                        </button>
                        <button
                          onClick={() => {
                            const end = endOfMonth(rangePickerMonth);
                            const start = startOfMonth(rangePickerMonth);
                            setCustomStartDate(start);
                            setCustomEndDate(end);
                            setDateSelectionMode('end');
                          }}
                          className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors whitespace-nowrap"
                        >
                          This Month
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              <span className="hidden md:inline mx-4 text-gray-500 font-medium">to</span>
              <span className="md:hidden text-center text-gray-500 font-medium text-sm">to</span>
              
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z"/>
                  </svg>
                </div>
                <input
                  id="datepicker-range-end"
                  name="end"
                  type="text"
                  readOnly
                  value={customEndDate ? format(customEndDate, 'MMM dd, yyyy') : ''}
                  onClick={() => {
                    setDateSelectionMode('end');
                    setShowCalendarDropdown(true);
                  }}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5 cursor-pointer hover:bg-gray-100 transition-colors"
                  placeholder="Select date end"
                />
                
                {/* Calendar Dropdown for End Date */}
                {showCalendarDropdown && dateSelectionMode === 'end' && (
                  <>
                    {/* Backdrop to close dropdown when clicking outside */}
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowCalendarDropdown(false)}
                    />
                    
                    {/* Calendar Popup - Centered on mobile, under input on desktop */}
                    <div className="fixed md:absolute left-1/2 md:left-0 top-1/2 md:top-full -translate-x-1/2 md:translate-x-0 -translate-y-1/2 md:translate-y-0 md:mt-2 z-20 bg-white rounded-lg shadow-xl border border-gray-200 p-3 w-[calc(100vw-2rem)] max-w-[280px] md:w-72">
                      {/* Calendar Navigation */}
                      <div className="flex items-center justify-between mb-3">
                        <button
                          onClick={handleRangePickerPreviousMonth}
                          className="p-1.5 hover:bg-gray-100 rounded transition-colors text-gray-700 flex-shrink-0"
                          title="Previous month"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <h4 className="text-sm font-semibold text-gray-900 truncate px-2">
                          {format(rangePickerMonth, 'MMMM yyyy')}
                        </h4>
                        <button
                          onClick={handleRangePickerNextMonth}
                          className="p-1.5 hover:bg-gray-100 rounded transition-colors text-gray-700 flex-shrink-0"
                          title="Next month"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>

                      {/* Calendar Grid */}
                      <div>
                        <div className="grid grid-cols-7 gap-1 mb-1">
                          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                            <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                              {day}
                            </div>
                          ))}
                        </div>
                        
                        <div className="grid grid-cols-7 gap-1">
                          {getDaysInRangePickerMonth().map((day, idx) => {
                            const isCurrentMonth = isSameMonth(day, rangePickerMonth);
                            const isTodayDate = isToday(day);
                            const isStart = isDateRangeStart(day);
                            const isEnd = isDateRangeEnd(day);
                            const inRange = isDateInRange(day);
                            
                            return (
                              <button
                                key={idx}
                                onClick={() => {
                                  if (isCurrentMonth) {
                                    handleRangeDateClick(day);
                                  }
                                }}
                                disabled={!isCurrentMonth}
                                className={`
                                  relative p-1.5 h-8 rounded text-xs font-medium transition-all
                                  ${!isCurrentMonth ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-blue-50 cursor-pointer'}
                                  ${isTodayDate && isCurrentMonth ? 'ring-1 ring-blue-500' : ''}
                                  ${isStart || isEnd ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}
                                  ${inRange && !isStart && !isEnd && isCurrentMonth ? 'bg-blue-100 text-blue-900' : ''}
                                `}
                              >
                                {format(day, 'd')}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Quick Select Options */}
                      <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 gap-1.5">
                        <button
                          onClick={() => {
                            const end = new Date();
                            const start = subMonths(end, 1);
                            setCustomStartDate(start);
                            setCustomEndDate(end);
                            setDateSelectionMode('end');
                          }}
                          className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors whitespace-nowrap"
                        >
                          Last Month
                        </button>
                        <button
                          onClick={() => {
                            const end = new Date();
                            const start = subMonths(end, 3);
                            setCustomStartDate(start);
                            setCustomEndDate(end);
                            setDateSelectionMode('end');
                          }}
                          className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors whitespace-nowrap"
                        >
                          Last 3M
                        </button>
                        <button
                          onClick={() => {
                            const end = new Date();
                            const start = subMonths(end, 6);
                            setCustomStartDate(start);
                            setCustomEndDate(end);
                            setDateSelectionMode('end');
                          }}
                          className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors whitespace-nowrap"
                        >
                          Last 6M
                        </button>
                        <button
                          onClick={() => {
                            const end = endOfMonth(rangePickerMonth);
                            const start = startOfMonth(rangePickerMonth);
                            setCustomStartDate(start);
                            setCustomEndDate(end);
                            setDateSelectionMode('end');
                          }}
                          className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors whitespace-nowrap"
                        >
                          This Month
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              <div className="flex gap-2 w-full md:w-auto md:ml-4">
                <button
                  onClick={() => {
                    if (customStartDate && customEndDate) {
                      // Jump the calendar to where the range starts.
                      selectPeriod('Custom', startOfMonth(customStartDate));
                      setShowCalendarDropdown(false);
                    } else {
                      alert('Please select both start and end dates');
                    }
                  }}
                  disabled={!customStartDate || !customEndDate}
                  className={`flex-1 md:flex-none px-4 py-2.5 text-sm rounded-lg font-medium transition-colors ${
                    customStartDate && customEndDate
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Apply
                </button>
                <button
                  onClick={() => {
                    setCustomStartDate(null);
                    setCustomEndDate(null);
                    setShowCalendarDropdown(false);
                    setDateSelectionMode('start');
                    selectPeriod('1M');
                  }}
                  className="flex-1 md:flex-none px-4 py-2.5 text-sm bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Details Modal */}
        {showPaymentDetails && selectedDate && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowPaymentDetails(false)}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-11/12 h-5/6 max-w-5xl flex flex-col overflow-hidden relative"
              onClick={e => e.stopPropagation()} // Prevent closing when clicking inside modal
            >
              {/* Close X Button */}
              <button
                onClick={() => setShowPaymentDetails(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none z-10"
                aria-label="Close"
              >
                &times;
              </button>
              <div className="p-4 h-full flex flex-col">
                {/* Summary Header */}
                <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white p-6 rounded-lg mb-6 text-center">
                  <h2 className="text-2xl font-bold mb-2">Payment Summary</h2>
                  <p className="text-lg mb-4">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</p>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold">{filteredPayments.length}</div>
                      <div className="text-sm opacity-90">Total Payments</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{formatCurrency(totalAmount)}</div>
                      <div className="text-sm opacity-90">Total Amount</div>
                    </div>
                  </div>
                </div>

                {/* Filter Controls */}
                <div className="bg-gray-50 p-4 rounded-lg mb-6 border">
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-semibold text-gray-700">Filter by Recorded By:</label>
                    <select
                      value={recordedByFilter}
                      onChange={(e) => setRecordedByFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm min-w-[200px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Drivers</option>
                      {Object.keys(users).map(userId => (
                        <option key={userId} value={userId}>
                          {getUserName(userId)}
                        </option>
                      ))}
                    </select>
                    {recordedByFilter && (
                      <button
                        onClick={() => setRecordedByFilter('')}
                        className="px-3 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded-md text-gray-600 transition-colors"
                      >
                        Clear Filter
                      </button>
                    )}
                  </div>
                </div>

                {/* Pagination controls and selector */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mr-2">Payments per page:</label>
                    <select
                      value={pageSize}
                      onChange={e => setPageSize(Number(e.target.value))}
                      className="border rounded px-2 py-1 text-xs"
                    >
                      {[20, 50, 100].map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-2 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
                    >
                      Prev
                    </button>
                    <span className="text-xs">{currentPage} / {totalPages || 1}</span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className="px-2 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>

                {/* Payments Table */}
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto max-h-[60vh]">
                  <table className="w-full border-collapse min-w-[900px]">
                    <thead>
                      <tr className="bg-gray-50 border-b-2 border-gray-200">
                        <th className="p-3 text-left font-semibold text-gray-700">Recipient</th>
                        <th className="p-3 text-left font-semibold text-gray-700">Tracking #</th>
                        <th className="p-3 text-left font-semibold text-gray-700">Time</th>
                        <th className="p-3 text-left font-semibold text-gray-700">Amount</th>
                        <th className="p-3 text-left font-semibold text-gray-700">Invoice #</th>
                        <th className="p-3 text-left font-semibold text-gray-700">Payment Method</th>
                        <th className="p-3 text-left font-semibold text-gray-700">Recorded By</th>
                        <th className="p-3 text-left font-semibold text-gray-700">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPayments.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="p-8 text-center text-gray-500">
                            {recordedByFilter ? 'No payments found for the selected driver' : 'No payments found for this date'}
                          </td>
                        </tr>
                      ) : (
                        paginatedPayments.map((payment) => {
                          const pkg = packages[payment.packageId] || {};
                          const methodInfo = getPaymentMethodInfo(payment.paymentMethod);

                          return (
                            <tr
                              key={payment.id}
                              onClick={() => navigateToPackage(payment.packageId)}
                              className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <td className="p-3">
                                <div className="font-medium text-gray-900">
                                  {pkg.recipient || 'N/A'}
                                </div>
                              </td>
                              <td className="p-3">
                                <div className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                                  {pkg.trackingNumber || 'N/A'}
                                </div>
                              </td>
                              <td className="p-3">
                                <div className="text-sm text-gray-600">
                                  {payment.paymentDate ? format(payment.paymentDate, 'h:mm a') : '—'}
                                </div>
                              </td>
                              <td className="p-3">
                                <div className="text-lg font-bold text-green-600">
                                  {formatCurrency(payment.amount)}
                                </div>
                              </td>
                              <td className="p-3">
                                <div className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                                  {payment.reference || '-'}
                                </div>
                              </td>
                              <td className="p-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${methodInfo.color}`}>
                                  <span>{methodInfo.icon}</span>
                                  {formatPaymentMethod(payment.paymentMethod)}
                                </span>
                              </td>
                              <td className="p-3 text-sm text-gray-600">
                                {getUserName(payment.recordedBy || payment.paidBy)}
                              </td>
                              <td className="p-3">
                                <div className="text-sm text-gray-600 max-w-[200px] truncate">
                                  {payment.notes || '-'}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards - Statistics and Time-Based Revenue (Top Row) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6 mb-4 md:mb-6">
          {/* Total Payments Card */}
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6 border border-gray-100">
            <h3 className="text-sm md:text-lg font-semibold text-gray-700 mb-3 md:mb-4">Total Payments</h3>
            <div className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">{formatCurrency(totalAmount)}</div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Paid</span>
                <span className="text-xs md:text-sm font-medium text-green-600">{formatCurrency(paidAmount)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Pending</span>
                <span className="text-xs md:text-sm font-medium text-orange-600">{formatCurrency(pendingAmount)}</span>
              </div>
            </div>
          </div>

          {/* Time-Based Revenue Card */}
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6 border border-gray-100">
            <h3 className="text-sm md:text-lg font-semibold text-gray-700 mb-3 md:mb-4">Time-Based Revenue</h3>
            <div className="space-y-2 md:space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Daily</span>
                <span className="text-base md:text-lg font-semibold text-blue-600">{formatCurrency(periodTotals.daily)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Weekly</span>
                <span className="text-base md:text-lg font-semibold text-blue-600">{formatCurrency(periodTotals.weekly)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Monthly</span>
                <span className="text-base md:text-lg font-semibold text-blue-600">{formatCurrency(periodTotals.monthly)}</span>
              </div>
            </div>
          </div>

          {/* Statistics Card */}
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6 border border-gray-100">
            <h3 className="text-sm md:text-lg font-semibold text-gray-700 mb-3 md:mb-4">Statistics</h3>
            <div className="space-y-2 md:space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Total Transactions</span>
                <span className="text-base md:text-lg font-semibold text-gray-900">{filteredPayments.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Avg. Transaction</span>
                <span className="text-base md:text-lg font-semibold text-gray-900">
                  {formatCurrency(filteredPayments.length > 0 ? totalAmount / filteredPayments.length : 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs md:text-sm text-gray-600">Selected Period</span>
                <span className="text-base md:text-lg font-semibold text-gray-900">
                  {formatCurrency(totalAmount)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid - Calendar and Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Calendar */}
          <div className="lg:col-span-2">
            {/* Calendar */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">
                    {format(currentMonth, 'MMMM yyyy')}
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={handlePreviousMonth}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={handleToday}
                      className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      Today
                    </button>
                    <button
                      onClick={handleNextMonth}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="p-4">
                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
                      {day}
                    </div>
                  ))}
                </div>
                
                <div className="grid grid-cols-7 gap-1">
                  {getDaysInMonth().map((day, idx) => {
                    const paymentCount = getPaymentCountForDate(day);
                    const dateKey = day.toISOString().substring(0, 10);
                    const isCurrentMonth = isSameMonth(day, currentMonth);
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    const hasPayments = paymentCount > 0;
                    const isHighlighted = highlightedDates.has(dateKey);
                    
                    return (
                      <div
                        key={idx}
                        onClick={() => handleDateClick(day)}
                        className={`
                          relative p-2 h-20 rounded-lg border cursor-pointer transition-all
                          ${!isCurrentMonth ? 'bg-gray-50 text-gray-400' : 'bg-white'}
                          ${isToday(day) ? 'border-blue-500 border-2' : 'border-gray-200'}
                          ${isSelected ? 'bg-blue-50 border-blue-500' : ''}
                          ${isHighlighted && isCurrentMonth ? 'bg-yellow-50 border-yellow-400 border-2' : ''}
                          ${hasPayments ? 'hover:bg-gray-50' : ''}
                        `}
                      >
                        <div className="text-sm font-medium">
                          {format(day, 'd')}
                        </div>
                        {hasPayments && isCurrentMonth && (
                          <div className="mt-1">
                            <div className={`text-xs font-medium ${isHighlighted ? 'text-yellow-600' : 'text-blue-600'}`}>
                              {paymentCount}
                              {isHighlighted && (
                                <span className="ml-1" title="Search match">🔍</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Summary and Filters */}
          <div className="lg:col-span-1 space-y-6">
            {/* Summary Cards */}
            <div className="space-y-4">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-500">{scopeLabel}</div>
                <div className="text-2xl font-bold text-blue-600 mt-1">{totalCount} Payments</div>
                {selectedDate && (
                  <button
                    onClick={() => setSelectedDate(null)}
                    className="text-xs text-blue-500 hover:text-blue-700 mt-2"
                  >
                    Clear date filter
                  </button>
                )}
              </div>
              
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-500">Total Amount</div>
                <div className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalAmount)}</div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-500">Average Payment</div>
                <div className="text-2xl font-bold text-purple-600 mt-1">
                  {formatCurrency(totalCount > 0 ? totalAmount / totalCount : 0)}
                </div>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="bg-white shadow rounded-lg">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-gray-700">Filters</h3>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Search */}
                <div>
                  <label className="text-sm font-medium text-gray-600">Search</label>
                  <input
                    type="text"
                    placeholder="Recipient, tracking #, amount..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="mt-1 border px-3 py-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {searchQuery && highlightedDates.size > 0 && (
                    <div className="mt-2 text-xs text-gray-600">
                      <span className="inline-flex items-center gap-1">
                        <span className="w-3 h-3 bg-yellow-50 border-2 border-yellow-400 rounded"></span>
                        Highlighted dates show search matches
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Payment Method Filter */}
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">Payment Method</label>
                  <div className="space-y-2">
                    <button
                      onClick={() => setPaymentMethodFilter('all')}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors
                        ${paymentMethodFilter === 'all' 
                          ? 'bg-gray-200 text-gray-800' 
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
                    >
                      All Methods
                    </button>
                    
                    {uniquePaymentMethods.map(method => {
                      const methodInfo = getPaymentMethodInfo(method);
                      return (
                        <button
                          key={method}
                          onClick={() => setPaymentMethodFilter(method)}
                          className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                            ${paymentMethodFilter === method 
                              ? methodInfo.color 
                              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}
                        >
                          <span>{methodInfo.icon}</span>
                          {formatPaymentMethod(method)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results Info */}
        {loading ? (
          <div className="mt-6 bg-white shadow rounded p-8 text-center">
            <p className="text-gray-500">Loading payment history...</p>
          </div>
        ) : (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-sm text-gray-700 text-center space-y-1">
              <p className="font-medium">Showing {filteredPayments.length} payments</p>
              <p className="text-xs text-gray-600">{scopeLabel}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;